import { Button, chakra, Flex, Icon } from '@chakra-ui/react';
import { useResource, useResources, useResourcesInfinite } from 'datx-jsonapi-react';
import { BaseEmoji } from 'emoji-mart';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';

import SendIcon from '@/assets/icons/ic-send-message.svg';

import { NewChatEmojiPicker } from '@/components/ui/shared/conversation/Chat/NewChatEmojiPicker';
import { getUnreadNotificationsQuery } from '@/components/ui/shared/notifications/NotificationBadge/NotificationBadge.queries';
import { ResourceType } from '@/enums/ResourceType';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useStores } from '@/hooks/useStores';
import { queryAllChatMessages } from '@/queries/chat-messages';
import { queryChatChannelOfUser } from '@/queries/event-chat-channels';
import { queryChatChannelNotification } from '@/queries/notifications';
import { ChatMessages } from '@/resources/ChatMessages';
import { EventChatChannel } from '@/resources/EventChatChannel';
import { Notification } from '@/resources/Notification';
import { ThinProfile } from '@/resources/ThinProfile';

const AutoResizeInput = chakra(TextareaAutosize);

interface IMessageInputProps {
  channel?: EventChatChannel;
  profile: ThinProfile;
}

interface IMessageFormProps {
  content: string;
}

export const MessageInput: FC<IMessageInputProps> = ({ channel, profile }) => {
  const { register, handleSubmit, reset, watch, setFocus, setValue, getValues } = useForm<IMessageFormProps>();
  const { store } = useStores();
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { mutate: mutateMessages } = useResourcesInfinite(queryAllChatMessages(channel?.id));
  const { mutate: mutateChannel } = useResource(queryChatChannelOfUser(profile.user.id, eventId));

  const { data: notifications } = useResources(queryChatChannelNotification(channel?.id));
  const { mutate: mutateNotificationsForChannel } = useResources(
    getUnreadNotificationsQuery(eventId, ResourceType.EventChatChannel)
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  async function submitMessage({ content }: IMessageFormProps) {
    try {
      setIsLoading(true);

      if (channel) {
        await sendMessageToChannel(content, channel.id);
        return;
      }

      const response = await store.request(
        `${EventChatChannel.type}?include=event_chat_channel_participants.user.main_profile`,
        'POST',
        {
          data: {
            type: EventChatChannel.type,
            attributes: {
              event_id: eventId,
              participant_ids: [profile.user.id],
            },
          },
        }
      );

      const eventChatChannel = response?.data as EventChatChannel;

      await sendMessageToChannel(content, eventChatChannel.id);

      await mutateChannel();
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  async function sendMessageToChannel(content: string, channelId: string) {
    try {
      await store.request(`${ChatMessages.type}`, 'POST', {
        data: {
          type: ChatMessages.type,
          attributes: {
            channel_id: channelId,
            content,
          },
        },
      });

      await mutateMessages();
      reset();
    } catch (err) {
      console.log(err);
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent new line
      handleSubmit((data) => {
        submitMessage(data);
      })();
    }
  };

  useEffect(() => {
    // this line will not trigger markNotificationAsRead because notifications are still loading, but we need focus anyway
    setFocus('content');
    if (notifications && notifications.length > 0) {
      markNotificationAsRead();
    }
  }, [notifications, setFocus]);

  async function markNotificationAsRead() {
    if (!channel) return;
    if (channel.unreadMessages > 0 && notifications.length > 0) {
      const channelNotification = notifications[0];

      try {
        await store.request(`${Notification.type}/${channelNotification.id}`, 'PUT', {
          data: {
            type: Notification.type,
            attributes: {
              read: true,
            },
          },
        });

        mutateNotificationsForChannel();
      } catch (err) {
        console.log(err);
      }
    }
  }

  const onEmojiSelect = (emoji: BaseEmoji) => {
    const currentContent = getValues('content');

    setValue('content', currentContent + emoji.native);
  };

  return (
    <form onSubmit={handleSubmit(submitMessage)}>
      <Flex alignItems="center" border="1px" overflow="hidden" borderColor="gray.200" py={3} px={2} borderRadius={4}>
        <AutoResizeInput
          resize="none"
          w="full"
          {...register('content', {
            required: true,
          })}
          onKeyDown={handleKeyDown}
          outline="none"
          borderRadius="4px"
          onFocus={markNotificationAsRead}
          px={2}
          mr={1}
          maxRows={5}
          _disabled={{ opacity: '0.6' }}
          // disabled={isLoading}
          placeholder="Write a message..."
        />

        <NewChatEmojiPicker onEmojiSelect={onEmojiSelect} />
        <Button
          variant="link"
          colorScheme="violet"
          aria-label="Send message"
          alignSelf="flex-end"
          maxW="min-content"
          minH={8}
          type="submit"
          disabled={!watch('content')}
          isLoading={isLoading}
        >
          <Icon w="24px" h="24px" ml={1} as={SendIcon} />
        </Button>
      </Flex>
    </form>
  );
};
